// Le card "consegna" e "ordine ricevuto" nella chat ospite: l'orario grande
// (in Fraunces), gli articoli, la nota dello staff. I testi delle etichette
// vengono da i18n (lingua dell'app); gli articoli arrivano dal server gia'
// nella lingua dell'ospite. Il testo del messaggio resta sotto come
// fallback per email e tablet.

import React from 'react';
import { useTranslation } from 'react-i18next';
import { Truck, CheckCircle2, ShoppingBag } from 'lucide-react';
import { intlLocale } from '../lib/locale';
import type { ChatMessageMeta } from '../lib/guestApi';

const TZ = 'Europe/Rome';

/** "Oggi" / "Domani" / "lunedì 28 settembre", nella lingua dell'app ('' se la data non e' valida). */
export function deliveryDayLabel(iso: string, lang: string, t: (k: string) => string): string {
  const d = new Date(iso);
  // Una data non valida farebbe lanciare Intl (RangeError) e cadere la card.
  if (isNaN(d.getTime())) return '';
  const key = (x: Date) => new Intl.DateTimeFormat('en-CA', { timeZone: TZ, year: 'numeric', month: '2-digit', day: '2-digit' }).format(x);
  const now = new Date();
  if (key(d) === key(now)) return t('guestChat.today');
  if (key(d) === key(new Date(now.getTime() + 86_400_000))) return t('guestChat.delivery.tomorrow');
  return new Intl.DateTimeFormat(intlLocale(lang), { weekday: 'long', day: 'numeric', month: 'long', timeZone: TZ }).format(d);
}

export function deliveryTimeLabel(iso: string, lang: string): string {
  const d = new Date(iso);
  if (isNaN(d.getTime())) return '';
  return new Intl.DateTimeFormat(intlLocale(lang), { hour: '2-digit', minute: '2-digit', hour12: false, timeZone: TZ }).format(d);
}

// ---- La cornice delle card in chat ------------------------------------------------
// Card della famiglia (come le pagine dei servizi): fondo avorio, angoli
// 20px, bordo sottile nella tinta della card, striscia colorata in cima,
// l'icona nel cerchio e l'occhiello maiuscolo. I colori restano quelli di
// sempre: azzurro l'ordine, ambra la consegna, verde il consegnato.

type CardTone = 'sky' | 'amber' | 'emerald';

const CARD_TONES: Record<CardTone, { box: string; strip: string; icon: string; label: string }> = {
  sky: {
    box: 'border-[#DCE6F2] shadow-[0_2px_12px_rgba(3,105,161,.06)] dark:border-sky-900/60',
    strip: 'bg-gradient-to-r from-sky-500 to-sky-600',
    icon: 'bg-sky-100 text-sky-700 dark:bg-sky-900/50 dark:text-sky-300',
    label: 'text-sky-800 dark:text-sky-200',
  },
  amber: {
    box: 'border-[#F3E2C4] shadow-[0_2px_12px_rgba(180,83,9,.06)] dark:border-amber-900/60',
    strip: 'bg-gradient-to-r from-amber-500 to-orange-600',
    icon: 'bg-amber-100 text-amber-700 dark:bg-amber-900/50 dark:text-amber-300',
    label: 'text-amber-800 dark:text-amber-200',
  },
  emerald: {
    box: 'border-[#D3E9DA] shadow-[0_2px_12px_rgba(4,120,87,.06)] dark:border-emerald-900/60',
    strip: 'bg-gradient-to-r from-emerald-500 to-emerald-600',
    icon: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/50 dark:text-emerald-300',
    label: 'text-emerald-800 dark:text-emerald-200',
  },
};

/** L'occhiello piccolo dentro le card ("IL TUO ORDINE"). */
const CARD_EYEBROW = 'm-0 text-[10.5px] font-extrabold uppercase leading-snug tracking-[0.14em] text-nva-muted dark:text-stone-400';

function ChatCardFrame({ tone, icon, label, time, children }: {
  tone: CardTone;
  icon: React.ReactNode;
  label: string;
  /** L'orario del messaggio, in basso a destra come nelle bolle. */
  time?: string;
  children?: React.ReactNode;
}) {
  const colors = CARD_TONES[tone];
  return (
    <div className={`w-full overflow-hidden rounded-[20px] border bg-nva-card dark:bg-stone-900 dark:shadow-none ${colors.box}`}>
      <div aria-hidden="true" className={`h-[5px] ${colors.strip}`} />
      <div className="flex flex-col gap-[9px] px-3.5 pb-2.5 pt-3">
        <p className={`m-0 flex items-center gap-2.5 text-[11px] font-extrabold uppercase leading-snug tracking-[0.14em] ${colors.label}`}>
          <span aria-hidden="true" className={`flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full [&>svg]:h-[17px] [&>svg]:w-[17px] ${colors.icon}`}>
            {icon}
          </span>
          <span className="min-w-0">{label}</span>
        </p>
        {children}
        {time ? (
          <span className="block select-none text-right text-[11px] leading-tight tabular-nums text-nva-muted dark:text-stone-400">{time}</span>
        ) : null}
      </div>
    </div>
  );
}

/** "lunedì 28 settembre" → "Lunedì 28 settembre" (solo la prima lettera). */
const capitalizeFirst = (s: string): string => (s ? s.charAt(0).toUpperCase() + s.slice(1) : s);

interface Props {
  meta: ChatMessageMeta;
  kind: 'delivery' | 'delivered';
  /**
   * Il testo del messaggio, per il promemoria (meta.status 'reminder'): la
   * card resta quella della consegna programmata e il promemoria ci sta
   * dentro, sotto l'intestazione.
   */
  text?: string;
  /** L'orario del messaggio (in basso a destra). */
  time?: string;
  /** Altro in fondo alla card, sopra l'orario (la traduzione). */
  children?: React.ReactNode;
}

export default function DeliveryCard({ meta, kind, text, time, children }: Props) {
  const { t, i18n } = useTranslation();
  const lang = i18n.language;
  const delivered = kind === 'delivered' || meta.status === 'delivered';
  const reminderText = !delivered && meta.status === 'reminder' && typeof text === 'string' ? text.trim() : '';
  const at = typeof meta.deliveryAt === 'string' ? meta.deliveryAt : null;
  const items = Array.isArray(meta.items) ? meta.items : [];
  const note = typeof meta.note === 'string' ? meta.note : '';
  const timeText = !delivered && at ? deliveryTimeLabel(at, lang) : '';

  return (
    <ChatCardFrame
      tone={delivered ? 'emerald' : 'amber'}
      icon={delivered ? <CheckCircle2 strokeWidth={2.1} /> : <Truck strokeWidth={2.1} />}
      label={delivered ? t('guestChat.delivery.delivered') : t('guestChat.delivery.scheduled')}
      time={time}
    >
      {reminderText && (
        <p className="m-0 text-[14.5px] font-semibold leading-snug text-amber-900 dark:text-amber-100 chat-text">{reminderText}</p>
      )}
      {timeText && (
        <p className="m-0 flex flex-wrap items-baseline gap-x-2.5 gap-y-1">
          {/* L'orario grande in Fraunces: e' la cosa che l'ospite cerca. */}
          <span className="font-display text-[34px] font-bold leading-none tabular-nums text-nva-espresso dark:text-stone-100">
            {timeText}
          </span>
          <span className="text-[15px] font-extrabold text-amber-800 dark:text-amber-200">
            {capitalizeFirst(deliveryDayLabel(at || '', lang, t))}
          </span>
        </p>
      )}
      {items.length > 0 && (
        <div className="flex flex-col gap-0.5 border-t border-nva-hairline pt-2 dark:border-stone-800">
          <p className={CARD_EYEBROW}>{t('guestChat.delivery.items')}</p>
          <ul className="m-0 flex list-none flex-col gap-0.5 p-0">
            {items.map((it, i) => (
              <li key={i} className="text-[14.5px] font-semibold leading-snug text-nva-espresso dark:text-stone-100 chat-text">{it.label}</li>
            ))}
          </ul>
        </div>
      )}
      {note && (
        <p className="m-0 rounded-[4px_12px_12px_4px] border-l-[3px] border-nva-gold bg-nva-cream px-2.5 py-2 text-[13.5px] leading-[1.4] text-nva-espresso dark:bg-nva-cream-dark dark:text-stone-100 chat-text">
          <span className="font-bold">{t('guestChat.delivery.note')}:</span> {note}
        </p>
      )}
      {children}
    </ChatCardFrame>
  );
}

/**
 * La card "ordine ricevuto": arriva appena l'ospite compra degli extra,
 * prima che lo staff fissi l'orario. Serve a rassicurare ("l'abbiamo
 * visto") e, se c'e' qualcosa da portare, a dire che l'orario arrivera' qui
 * in chat. Stessa cornice della card di consegna, azzurra per non
 * confonderla con un orario gia' fissato. Gli articoli arrivano dal server
 * gia' nella lingua dell'ospite. Il totale non si mostra: la ricevuta e la
 * lista degli extra lo dicono gia', con la valuta giusta.
 */
export function OrderCard({ meta, time, children }: { meta: ChatMessageMeta; time?: string; children?: React.ReactNode }) {
  const { t } = useTranslation();
  const items = (Array.isArray(meta.items) ? meta.items : []).filter(
    (it) => !!it && typeof it.label === 'string' && it.label.trim() !== ''
  );

  return (
    <ChatCardFrame tone="sky" icon={<ShoppingBag strokeWidth={2.1} />} label={t('guestChat.order.received')} time={time}>
      {items.length > 0 && (
        <ul className="m-0 flex list-none flex-col gap-1 p-0">
          {items.map((it, i) => (
            <li key={i} className="flex items-start gap-2 text-[15px] font-bold leading-snug text-nva-espresso dark:text-stone-100 chat-text">
              <span aria-hidden="true" className="mt-[9px] h-[5px] w-[5px] flex-shrink-0 rounded-full bg-sky-700 dark:bg-sky-300" />
              {/* Il server scrive gia' "2× Culla": la quantita' si aggiunge solo se manca. */}
              <span className="min-w-0">
                {typeof it.qty === 'number' && it.qty > 1 && it.label.indexOf(`${it.qty}×`) !== 0 ? `${it.qty}× ` : ''}{it.label}
              </span>
            </li>
          ))}
        </ul>
      )}
      {meta.deliverable === true && (
        <p className="m-0 flex items-start gap-2 text-[13.5px] leading-[1.35] text-nva-muted dark:text-stone-300">
          <Truck className="mt-px h-4 w-4 flex-shrink-0 text-sky-700 dark:text-sky-300" strokeWidth={2.1} aria-hidden="true" />
          <span className="min-w-0">{t('guestChat.order.willMessage')}</span>
        </p>
      )}
      {children}
    </ChatCardFrame>
  );
}

/** Chip compatto per la lista degli extra acquistati (MyStay, Extras). */
export function DeliveryChip({ deliveryAt, deliveryStatus }: { deliveryAt?: string | null; deliveryStatus?: string | null }) {
  const { t, i18n } = useTranslation();
  if (!deliveryStatus || deliveryStatus === 'cancelled') return null;
  const delivered = deliveryStatus === 'delivered';
  // "Consegnato" non ha bisogno dell'orario; quello programmato si'.
  const at = deliveryAt || '';
  if (!delivered && !at) return null;
  return (
    <span
      className={`inline-flex items-center gap-1 mt-1 px-2 py-0.5 rounded-full text-[11px] font-semibold ${
        delivered
          ? 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/40 dark:text-emerald-200'
          : 'bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-200'
      }`}
    >
      {delivered ? '✅' : '🚚'}{' '}
      {delivered
        ? t('guestChat.delivery.delivered')
        : `${deliveryDayLabel(at, i18n.language, t)} ${deliveryTimeLabel(at, i18n.language)}`}
    </span>
  );
}
